import React, { useState, useMemo, useEffect } from 'react';
import { BiSort } from 'react-icons/bi';
import SearchBar from '@components/Layout/SearchBar';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    CREATE_DEVICE_PATH,
    INDIVIDUAL_CREATE_DEVICE_PATH,
} from '@constants/routes';
import { useDispatch } from 'react-redux';
import { FaFilter } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { IoMdArrowRoundBack } from 'react-icons/io';
import DeviceManagementTable from '@components/Layout/DeviceManagementTable';
import DownIcon from '@components/SvgComponents/DownIcon';
import UpIcon from '@components/SvgComponents/UpIcon';
import { getUserAttributes } from '@utils/authUtils';
import { TENANT_ID } from '@constants/cognitoAttributes';
import { deviceList, individualDeviceList } from '@utils/utils';
import { useAppSelector } from '@store/hooks';
import DataLoader from '@components/DataLoader';
import { setTreeViewSelectedValue } from '@store/reducers/userSlice';
import { INDIVIDUAL } from '@constants/constants';

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn text-white py-2 bg-primary"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));

interface DeviceData {
    id?: string;
    device_name?: string;
    device_id?: string;
    device_status?: string;
    status?: string;
    created_at?: string;
    created_at_time?: string;
    created_by_name?: string;
    created_by?: string;
}

const DeviceManagementList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [sort, setSort] = useState('newest');
    const [filter, setFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [deviceData, setDeviceData] = useState<DeviceData[]>([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [isSortExpanded, setIsSortExpanded] = useState(true);
    const [isInfusionExpanded, setIsInfusionExpanded] = useState(true);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );

    const fetchAttributes = async () => {
        // const attributes = await getUserAttributes();

        // if (typeof attributes !== 'string' && attributes !== null) {
        //     const tenant = attributes[TENANT_ID] ?? '';
        try {
            if (loggedInUserRole.toLowerCase() == INDIVIDUAL.toLowerCase()) {
                const data = await individualDeviceList(loggedInUserTenantID);
                setDeviceData(data);
            } else {
                const data = await deviceList(loggedInUserTenantID);
                setDeviceData(data);
            }
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setLoading(false);
        }
        // }
    };

    useEffect(() => {
        fetchAttributes();
    }, []);

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('device_list_table.device_name'),
        },
        {
            id: 2,
            title: t('device_list_table.device_id'),
        },
        {
            id: 3,
            title: t('device_list_table.device_status'),
        },

        {
            id: 4,
            title: t('device_list_table.status'),
        },
        {
            id: 5,
            title: t('device_list_table.action'),
        },
        // Add more user objects here
    ]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSort(event);
    };

    const handleFilterChange = (value: string) => {
        setFilter(value);
        // setStatusFilter(value);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };
    const handleCloseModal = (event: any) => {
        event.stopPropagation();
        setIsDropdownVisible(false);
    };
    const handleAddDevice = () => {
        if (loggedInUserRole.toLowerCase() == INDIVIDUAL.toLowerCase()) {
            navigate(INDIVIDUAL_CREATE_DEVICE_PATH);
        } else {
            dispatch(setTreeViewSelectedValue(''));
            navigate(CREATE_DEVICE_PATH);
        }
    };
    const backArrow = () => {
        navigate(-1);
    };

    const toggleSortExpand = () => {
        setIsSortExpanded(!isSortExpanded);
    };
    const toggleInfusionExpand = () => {
        setIsInfusionExpanded(!isInfusionExpanded);
    };

    const filteredUsers = useMemo(() => {
        let filtered = deviceData;

        if (searchTerm) {
            filtered = filtered.filter(
                (device) =>
                    device.device_id
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    device.device_name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    device.status
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        if (filterField) {
            if (filterField.toLowerCase() == 'all') {
                filtered;
            } else if (filterField.toLowerCase() === 'active') {
                filtered = filtered.filter(
                    (user) =>
                        user.status
                            ?.toLowerCase()
                            .includes(filterField.toLowerCase()) &&
                        user.status?.toLowerCase() !== 'inactive'
                );
            } else if (filterField.toLowerCase() === 'inactive') {
                filtered = filtered.filter(
                    (user) =>
                        user.status
                            ?.toLowerCase()
                            .includes(filterField.toLowerCase()) &&
                        user.status?.toLowerCase() !== 'active'
                );
            } else {
                // Exclude 'inactive' status explicitly
                filtered = filtered.filter((user) =>
                    user.status
                        ?.toLowerCase()
                        .includes(filterField.toLowerCase())
                );
            }
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, deviceData]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField !== undefined && sortField !== null) {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.created_at_time);
                const dateB = new Date(b.created_at_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() == 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() == 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 3;

        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(renderPageButton(i));
            }
        } else {
            if (currentPage <= maxVisiblePages) {
                // Case: currentPage is within the first 'maxVisiblePages'
                for (let i = 1; i <= maxVisiblePages + 1; i++) {
                    pageNumbers.push(renderPageButton(i));
                }
                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            } else if (currentPage >= totalPages - maxVisiblePages + 1) {
                // Case: currentPage is near the end
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());
                for (
                    let i = totalPages - maxVisiblePages + 1;
                    i <= totalPages;
                    i++
                ) {
                    pageNumbers.push(renderPageButton(i));
                }
            } else {
                // Case: currentPage is somewhere in the middle
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());

                const startPage = currentPage - 1;
                const endPage = currentPage + 1;

                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(renderPageButton(i));
                }

                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            }
        }

        return pageNumbers;
    };

    const renderPageButton = (pageNumber: any) => {
        return (
            <button
                key={pageNumber}
                className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
                onClick={() => handlePageChange(pageNumber)}
            >
                {pageNumber}
            </button>
        );
    };

    const renderEllipsis = () => {
        return (
            <span
                key="ellipsis"
                className="bg-primary pagination-padding py-2 border rounded"
            >
                ...
            </span>
        );
    };

    const handleClear = (event: any) => {
        event.stopPropagation();
        setSortField('newest');
        setSort('newest');
        setFilterField('');
        setFilter('');
        handleCloseModal(event);
    };

    const handleSave = (event: any) => {
        event.stopPropagation();
        setSortField(sort);
        setFilterField(filter);
        handleCloseModal(event);
    };

    const handleDropdownToggle = (event: any) => {
        event.stopPropagation();
        // Prevent the default behavior to keep the dropdown open
        if (event && event.target) {
            // Check if the event target has the data-click-type attribute
            const isButtonClick = event.target.getAttribute('data-click-type');

            if (
                isButtonClick === 'btn-clear' ||
                isButtonClick === 'btn-save' ||
                isButtonClick === 'btn-close'
            ) {
                setIsDropdownVisible(false);
            } else {
                // setIsDropdownVisible(true);
                setIsDropdownVisible(!isDropdownVisible);
            }
        }
    };

    return (
        <>
            <div className="2xl:max-w-full">
                <div className="flex justify-content-between align-items-center">
                    <div
                        className="flex items-center cursor-pointer"
                        // onClick={backArrow}
                    >
                        {/* <IoMdArrowRoundBack className="w-8 h-8" /> */}
                        <h1 className="text-lg font-semibold ml-2">
                            {t('management.device.title')}
                        </h1>
                    </div>
                    <button
                        className="btn text-white py-2 px-3 bg-primary"
                        onClick={handleAddDevice}
                    >
                        {t('management.device.device_btn')}
                    </button>
                </div>
                <div className="flex justify-content-between align-items-center my-4">
                    <SearchBar
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                    <div className="">
                        <Dropdown
                            show={isDropdownVisible}
                            onClick={(event) => handleDropdownToggle(event)}
                            data-click-type="btn-open"
                        >
                            <Dropdown.Toggle
                                as={CustomDropdownToggle}
                                variant="primary"
                                id="dropdown-basic"
                            >
                                {t('user_management.hospital_user.filter')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                                <span className="ml-4 font-bold text-black text-sm">
                                    {t('management.device.filter')}
                                </span>
                                <button
                                    className="absolute right-0 top-0.5 cursor-pointer"
                                    onClick={(event) => handleCloseModal(event)}
                                    data-click-type="btn-close"
                                >
                                    <span className="absolute text-xs top-1.5 right-0 mr-3 cursor-pointer flex items-center">
                                        {t('management.device.close')}
                                        <IoClose className="ml-1" />
                                    </span>
                                </button>
                                <Dropdown.Header
                                    className="font-bold text-black  text-sm d-flex justify-content-between align-items-center"
                                    onClick={toggleSortExpand}
                                >
                                    {t('management.device.sort_bt')}
                                    {isSortExpanded ? <DownIcon /> : <UpIcon />}
                                </Dropdown.Header>
                                {isSortExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('newest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="newest"
                                                value="newest"
                                                aria-label="newest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'newest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.newest'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('oldest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="oldest"
                                                value="oldest"
                                                aria-label="oldest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'oldest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.oldest'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}
                                <Dropdown.Header
                                    className="font-bold text-black text-sm d-flex justify-content-between align-items-center"
                                    onClick={toggleInfusionExpand}
                                >
                                    {t('management.device.select_status')}
                                    {isInfusionExpanded ? (
                                        <DownIcon />
                                    ) : (
                                        <UpIcon />
                                    )}
                                </Dropdown.Header>
                                {isInfusionExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('all')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="all"
                                                value="all"
                                                aria-label="all"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'all'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.device.all')}
                                        </Dropdown.Item>
                                        {/* <Dropdown.Item
                                        className="flex items-center"
                                        onClick={() =>
                                            handleFilterChange('active')
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name="active"
                                            value="active"
                                            aria-label="active"
                                            checked={
                                                filter.toLowerCase() == 'active'
                                            }
                                            className="mr-2"
                                            readOnly
                                        />
                                        {t('management.device.active')}
                                    </Dropdown.Item> */}
                                        {/* <Dropdown.Item
                                        className="flex items-center"
                                        onClick={() =>
                                            handleFilterChange('inactive')
                                        }
                                    >
                                        <input
                                            type="radio"
                                            name="in active"
                                            value="in active"
                                            aria-label="in active"
                                            checked={
                                                filter.toLowerCase() ==
                                                'inactive'
                                            }
                                            className="mr-2"
                                            readOnly
                                        />
                                        {t('management.device.in_active')}
                                    </Dropdown.Item> */}
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('approved')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="approved"
                                                value="approved"
                                                aria-label="approved"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'approved'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.device.approved')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('pending')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="pending"
                                                value="pending"
                                                aria-label="pending"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'pending'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.device.pending')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('blocked')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="blocked"
                                                value="blocked"
                                                aria-label="blocked"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'blocked'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.device.blocked')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange(
                                                    'non functional'
                                                )
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="non_functional"
                                                value="non_functional"
                                                aria-label="non_functional"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'non functional'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'management.device.non_functional'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <div className="flex cursor-pointer align-items-center justify-content-center space-x-4 px-4 py-2">
                                    <button
                                        className="bg-white border-2 border-primary  text-primary px-4 py-1 rounded"
                                        onClick={(event) => handleClear(event)}
                                        data-click-type="btn-clear"
                                    >
                                        {t('management.device.clear')}
                                    </button>
                                    <button
                                        className="text-white bg-primary rounded px-4 py-1"
                                        onClick={(event) => handleSave(event)}
                                        data-click-type="btn-save"
                                    >
                                        {t('management.device.apply')}
                                    </button>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                <DeviceManagementTable
                    data={paginatedUsers}
                    header={tableHeading}
                    row={rowsPerPage}
                    totalData={deviceData}
                />
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default DeviceManagementList;
